import useResolver from './useResolver';
import ParticipationStore from '../store/ParticipationStore';
import ProjectStore from '../store/ProjectStore';
import PersonStore from '../store/PersonStore';
import OrganizationStore from '../store/OrganizationStore';
import { useState } from 'react';
import useToast from './useToast';
import Person from '../domain/Person';
import Project from '../domain/Project';
import Participation from '../domain/Participation';
import { exportToCsv } from '../ui/DataGrid/DataGrid.utils';
import { t } from '../i18n';
import Organization from '../domain/Organization';

export default function useExportParticipations() {
  const participationStore = useResolver(ParticipationStore);
  const projectStore = useResolver(ProjectStore);
  const personStore = useResolver(PersonStore);
  const organizationStore = useResolver(OrganizationStore);
  const [exporting, setExporting] = useState<boolean>(false);
  const toast = useToast();

  const handleExport = async (
    organizations: Organization[],
    fileName: string,
  ) => {
    try {
      setExporting(true);

      await Promise.all([
        participationStore.loadByCurrentOrganization(),
        projectStore.loadByActiveOrganization(),
        personStore.loadCurrentAndRelatedOrganizationPersons(),
      ]);
      const persons: Person[] = personStore.items
        .map((p) => {
          p.organization = organizationStore.getById(p.organizationId);

          // Not in provided organizations - ignore
          if (!organizations.find((o) => o.id === p.organizationId)) {
            p.organization = null;
          }

          return p;
        })
        .filter((p) => !p.isDisabled() && !!p.organization);

      const participations = participationStore.items;
      const projects = projectStore.ongoingProjects;

      exportParticipationsCsv(fileName, {
        participations,
        persons,
        projects,
      });
    } catch (error) {
      toast({
        status: 'error',
        title: t('Export failed'),
        description: String(error),
      });
    } finally {
      setExporting(false);
    }
  };

  return {
    exporting,
    onExport: handleExport,
  };
}

function exportParticipationsCsv(
  fileName: string,
  {
    participations,
    projects,
    persons,
  }: {
    projects: Project[];
    persons: Person[];
    participations: Participation[];
  },
) {
  exportToCsv(
    [...persons].sort((a, b) => {
      const orgAName = a.organization ? a.organization.name : '';
      const orgBName = b.organization ? b.organization.name : '';
      if (orgAName < orgBName) return -1;
      if (orgAName > orgBName) return 1;
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    }),
    {
      client: {
        title: t('Client'),
        accessor: (person) => person.organization?.name,
      },
      name: {
        title: t('Name'),
        accessor: (person) => person.name,
      },
      ...Object.assign(
        {},
        ...projects.map((project) => ({
          [project.id]: {
            title: project.name,
            hidden: true,
            hiddenInExport: false,
            accessor: (person: Person) => {
              return participations.some(
                (p) => p.projectId === project.id && p.personId === person.id,
              )
                ? '+'
                : null;
            },
          },
        })),
      ),
    },
    fileName,
  );
}
